import { PRIORITY_ATTRIBUTES } from './constants';
import { isSensitiveAttribute, safeGetText } from './pii-guard';
import { buildSelectorPath, filterHumanClasses } from './selector-builder';

export interface SerializedElement {
  tag: string;
  id: string | null;
  classes: string[];
  text: string;
  attributes: Record<string, string>;
  position: { x: number; y: number; w: number; h: number };
}

export interface SerializedAncestor {
  tag: string;
  id: string | null;
  classes: string[];
  veoTag: string | null;
}

export interface AutocapturePayload {
  $event_type: 'click' | 'submit' | 'change';
  $element: SerializedElement;
  $ancestors: SerializedAncestor[];
  $selector_path: string;
  $page_url: string;
  $page_path: string;
}

interface SerializeConfig {
  maxTextLength: number;
  maskAllText: boolean;
  captureElementMetadata: boolean;
}

/** Extrae los atributos relevantes del elemento, filtrando los sensibles. */
function extractPriorityAttributes(element: Element): Record<string, string> {
  const result: Record<string, string> = {};

  for (const attrName of PRIORITY_ATTRIBUTES) {
    const value = element.getAttribute(attrName);
    if (value !== null && !isSensitiveAttribute(attrName) && value.length < 256) {
      result[attrName] = value;
    }
  }

  return result;
}

/** Serializa un elemento DOM a un objeto plano para enviar al backend. */
export function serializeElement(element: Element, config: SerializeConfig): SerializedElement {
  const rect = element.getBoundingClientRect();

  return {
    tag: element.tagName.toLowerCase(),
    id: element.id || null,
    classes: filterHumanClasses(element),
    text: safeGetText(element, config.maxTextLength, config.maskAllText),
    attributes: config.captureElementMetadata ? extractPriorityAttributes(element) : {},
    position: {
      x: Math.round(rect.left + window.scrollX),
      y: Math.round(rect.top + window.scrollY),
      w: Math.round(rect.width),
      h: Math.round(rect.height),
    },
  };
}

/** Captura los ancestros del elemento (para tagging contextual en el dashboard). */
export function serializeAncestors(element: Element, maxDepth: number): SerializedAncestor[] {
  const ancestors: SerializedAncestor[] = [];
  let current: Element | null = element.parentElement;
  let depth = 0;

  while (current && current !== document.documentElement && depth < maxDepth) {
    ancestors.push({
      tag: current.tagName.toLowerCase(),
      id: current.id || null,
      classes: filterHumanClasses(current),
      veoTag: current.getAttribute('data-veo-tag') || null,
    });
    current = current.parentElement;
    depth++;
  }

  return ancestors;
}

/** Construye el payload completo para el evento `$autocapture`. */
export function buildAutocapturePayload(
  eventType: 'click' | 'submit' | 'change',
  element: Element,
  config: SerializeConfig & { captureAncestors: number },
): AutocapturePayload {
  return {
    $event_type: eventType,
    $element: serializeElement(element, config),
    $ancestors: serializeAncestors(element, config.captureAncestors),
    $selector_path: buildSelectorPath(element, config.captureAncestors),
    $page_url: window.location.href,
    $page_path: window.location.pathname,
  };
}
